import { Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { PROVIDER_STATUS_PAGES, ProviderComponentState, ProviderStatus, StatusProvider } from '@shared/ProviderStatus';
import dayjs from 'dayjs';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { catchError, exhaustMap, map, of, switchMap, timeout, timer } from 'rxjs';
import { ProviderStatusService } from '../../../services/provider-status.service';

@Component({
  selector: 'app-provider-status',
  templateUrl: './provider-status.component.html',
  imports: [ButtonModule, MessageModule],
  styles: [`
    :host { display: block; }
    .status-banner {
      margin-bottom: 1.25rem;
      border: 1px solid var(--p-message-warn-border-color);
      border-inline-start: 6px solid var(--p-orange-500);
      border-radius: .5rem;
      background: var(--p-message-warn-background);
      box-shadow: 0 3px 12px rgb(0 0 0 / 8%);
    }
    .status-banner.outage {
      border-color: var(--p-message-error-border-color);
      border-inline-start-color: var(--p-red-500);
      background: var(--p-message-error-background);
    }
    .status-heading { display: flex; align-items: baseline; gap: .75rem; }
    .status-icon { font-size: 1.5rem; flex-shrink: 0; }
    .status-title { font-size: 1.125rem; font-weight: 700; }
    .status-line { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem; padding: 0 .75rem .5rem; }
    .status-details { padding: .75rem; border-top: 1px solid var(--p-content-border-color); }
  `]
})
export class ProviderStatusComponent {
  public readonly provider = input.required<StatusProvider>();
  private readonly providerStatus = inject(ProviderStatusService);
  protected readonly status = signal<ProviderStatus | null>(null);
  protected readonly requestFailed = signal(false);
  protected readonly expanded = signal(false);
  protected readonly providerName = computed(() => ({
    openai: 'OpenAI', elevenlabs: 'ElevenLabs', did: 'D-ID', heygen: 'HeyGen', liveavatar: 'LiveAvatar', falai: 'fal.ai'
  })[this.provider()]);
  protected readonly avatarProvider = computed(() => this.provider() === 'heygen' || this.provider() === 'liveavatar');
  protected readonly statusPageUrl = computed(() => PROVIDER_STATUS_PAGES[this.provider()]);
  protected readonly detailsId = computed(() => `${this.provider()}-status-details`);
  protected readonly unknown = computed(() => this.requestFailed() || this.status()?.state === 'unknown');
  protected readonly visible = computed(() => this.unknown() || (this.status() !== null && this.status()?.state !== 'operational'));
  protected readonly severity = computed(() => this.status()?.state === 'outage' && !this.unknown() ? 'error' : 'warn');
  protected readonly title = computed(() => {
    if (this.provider() === 'did') {
      if (this.unknown()) return $localize`:@@did-status-unknown:D-ID-Status derzeit nicht verfügbar`;
      switch (this.status()?.state) {
        case 'outage': return $localize`:@@did-status-outage:D-ID meldet einen API-Ausfall`;
        case 'degraded': return $localize`:@@did-status-degraded:D-ID meldet API-Einschränkungen`;
        case 'maintenance': return $localize`:@@did-status-maintenance:D-ID meldet API-Wartungsarbeiten`;
        default: return '';
      }
    }
    if (this.avatarProvider()) {
      const name = this.providerName();
      if (this.unknown()) return $localize`:@@avatar-provider-status-unknown:${name}-Status derzeit nicht verfügbar`;
      switch (this.status()?.state) {
        case 'outage': return $localize`:@@avatar-provider-status-outage:${name} meldet einen API-Ausfall`;
        case 'degraded': return $localize`:@@avatar-provider-status-degraded:${name} meldet API-Einschränkungen`;
        case 'maintenance': return $localize`:@@avatar-provider-status-maintenance:${name} meldet API-Wartungsarbeiten`;
        default: return '';
      }
    }
    if (this.provider() === 'falai') {
      if (this.unknown()) return $localize`:@@falai-status-unknown:fal.ai-Status derzeit nicht verfügbar`;
      switch (this.status()?.state) {
        case 'outage': return $localize`:@@falai-status-outage:fal.ai meldet einen Ausfall`;
        case 'degraded': return $localize`:@@falai-status-degraded:fal.ai meldet Einschränkungen`;
        case 'maintenance': return $localize`:@@falai-status-maintenance:fal.ai meldet Wartungsarbeiten`;
        default: return '';
      }
    }
    if (this.provider() === 'elevenlabs') {
      if (this.unknown()) return $localize`:@@elevenlabs-status-unknown:ElevenLabs-Status derzeit nicht verfügbar`;
      switch (this.status()?.state) {
        case 'outage': return $localize`:@@elevenlabs-status-outage:ElevenLabs meldet einen Ausfall`;
        case 'degraded': return $localize`:@@elevenlabs-status-degraded:ElevenLabs meldet Einschränkungen`;
        case 'maintenance': return $localize`:@@elevenlabs-status-maintenance:ElevenLabs meldet Wartungsarbeiten`;
        default: return '';
      }
    }
    if (this.unknown()) return $localize`:@@openai-status-unknown:OpenAI-Status derzeit nicht verfügbar`;
    switch (this.status()?.state) {
      case 'outage': return $localize`:@@openai-status-outage:OpenAI meldet einen API-Ausfall`;
      case 'degraded': return $localize`:@@openai-status-degraded:OpenAI meldet API-Einschränkungen`;
      case 'maintenance': return $localize`:@@openai-status-maintenance:OpenAI meldet API-Wartungsarbeiten`;
      default: return '';
    }
  });

  constructor() {
    toObservable(this.provider).pipe(
      switchMap(provider => {
        this.status.set(null);
        this.requestFailed.set(false);
        this.expanded.set(false);
        return timer(0, 60_000).pipe(
          exhaustMap(() => this.providerStatus.getStatus(provider).pipe(
            timeout(10_000),
            map(status => ({ status, failed: false })),
            catchError(error => {
              console.warn(`Could not load ${provider} provider status`, error);
              return of({ status: null, failed: true });
            })
          ))
        );
      }),
      takeUntilDestroyed()
    ).subscribe(result => {
      this.requestFailed.set(result.failed);
      if (result.status) this.status.set(result.status);
    });
  }

  protected formatTime(value: string): string {
    return dayjs(value).format('DD.MM.YYYY HH:mm:ss');
  }

  protected componentState(state: ProviderComponentState): string {
    switch (state) {
      case 'operational': return $localize`:@@openai-component-operational:Keine Störung gemeldet`;
      case 'degraded_performance': return $localize`:@@openai-component-degraded:Verlangsamter Betrieb`;
      case 'partial_outage': return $localize`:@@openai-component-partial:Teilausfall`;
      case 'major_outage': return $localize`:@@openai-component-outage:Ausfall`;
      case 'under_maintenance': return $localize`:@@openai-component-maintenance:Wartungsarbeiten`;
    }
  }
}
